import React, { useEffect, useMemo, useState } from 'react';
import '../styles/Resume.css';
import { track } from '../lib/analytics';

const INLINE_TOKEN_RE = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`)/g;

const parseInline = (text, keyPrefix, { onLinkClick } = {}) => {
  const raw = String(text || '');
  const parts = raw.split(INLINE_TOKEN_RE).filter(Boolean);
  const nodes = [];

  parts.forEach((part, i) => {
    // [label](url)
    if (part.startsWith('[') && part.includes('](') && part.endsWith(')')) {
      const labelEnd = part.indexOf('](');
      const label = part.slice(1, labelEnd);
      const url = part.slice(labelEnd + 2, -1);
      nodes.push(
        <a
          key={`${keyPrefix}-link-${i}`}
          href={url}
          target="_blank"
          rel="noreferrer"
          onClick={() => onLinkClick?.({ label, url })}
        >
          {label}
        </a>
      );
      return;
    }

    // **bold**
    if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
      nodes.push(<strong key={`${keyPrefix}-b-${i}`}>{part.slice(2, -2)}</strong>);
      return;
    }

    // `code`
    if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
      nodes.push(<code key={`${keyPrefix}-code-${i}`}>{part.slice(1, -1)}</code>);
      return;
    }

    nodes.push(part);
  });

  return nodes;
};

const parseMarkdown = (markdown) => {
  const md = String(markdown || '').replace(/\r\n/g, '\n');
  const lines = md.split('\n');

  const blocks = [];
  let paragraph = [];
  let listItems = null;

  const flushParagraph = () => {
    const text = paragraph.join(' ').trim();
    paragraph = [];
    if (text) blocks.push({ type: 'p', text });
  };

  const flushList = () => {
    if (!listItems) return;
    const items = listItems
      .map((x) => x.trim())
      .filter(Boolean);
    listItems = null;
    if (items.length > 0) blocks.push({ type: 'ul', items });
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, '');

    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }

    if (line.trim() === '---') {
      flushParagraph();
      flushList();
      blocks.push({ type: 'hr' });
      continue;
    }

    const headingMatch = /^(#{1,3})\s+(.+)$/.exec(line.trim());
    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push({ type: `h${headingMatch[1].length}`, text: headingMatch[2].trim() });
      continue;
    }

    const listMatch = /^[-*]\s+(.+)$/.exec(line.trim());
    if (listMatch) {
      flushParagraph();
      if (!listItems) listItems = [];
      listItems.push(listMatch[1]);
      continue;
    }

    // Continuation line (for paragraphs or list items).
    if (listItems && (/^\s+/.test(line) || line.trim().startsWith('('))) {
      const lastIndex = listItems.length - 1;
      listItems[lastIndex] = `${listItems[lastIndex]} ${line.trim()}`;
      continue;
    }

    paragraph.push(line.trim());
  }

  flushParagraph();
  flushList();

  return blocks;
};

const splitResumeBlocks = (blocks) => {
  let i = 0;
  let name = null;
  let title = null;
  let location = null;
  let contacts = null;

  if (blocks[i]?.type === 'h1') {
    name = blocks[i].text;
    i += 1;
  }

  if (blocks[i]?.type === 'p') {
    title = blocks[i].text;
    i += 1;
  }

  if (blocks[i]?.type === 'p') {
    location = blocks[i].text;
    i += 1;
  }

  if (blocks[i]?.type === 'ul') {
    contacts = blocks[i].items;
    i += 1;
  }

  return {
    header: { name, title, location, contacts },
    body: blocks.slice(i),
  };
};

const normalizeSectionKey = (text) =>
  String(text || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const splitByH2 = (blocks) => {
  const sections = [];
  let current = null;

  const flush = () => {
    if (!current) return;
    // Only keep true sections (must have a title).
    if (current.title && current.blocks.length > 0) sections.push(current);
    current = null;
  };

  for (const block of blocks) {
    if (block.type === 'h2') {
      flush();
      current = { title: block.text, key: normalizeSectionKey(block.text), blocks: [block] };
      continue;
    }

    if (!current) {
      // Ignore any loose content before the first H2.
      continue;
    }

    current.blocks.push(block);
  }

  flush();
  return sections;
};

const VIEW = {
  work: { key: 'work experience', label: 'Work Experience' },
  education: { key: 'education', label: 'Education' },
  languages: { key: 'languages', label: 'Languages' },
};

const Resume = ({ mode = 'work' }) => {
  const pdfUrl = useMemo(() => `${process.env.PUBLIC_URL || ''}/resume.pdf`, []);
  const mdFile = useMemo(() => {
    const cleaned = String(mode || 'work').trim().toLowerCase();
    if (cleaned === 'education') return 'resume.education.md';
    if (cleaned === 'languages') return 'resume.languages.md';
    return 'resume.work.md';
  }, [mode]);
  const mdUrl = useMemo(() => `${process.env.PUBLIC_URL || ''}/${mdFile}`, [mdFile]);
  const [pdfAvailable, setPdfAvailable] = useState('unknown'); // unknown | yes | no
  const [mdState, setMdState] = useState({ status: 'loading', text: '' }); // loading | ready | missing

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch(pdfUrl, { method: 'HEAD' });
        if (cancelled) return;
        setPdfAvailable(res.ok ? 'yes' : 'no');
      } catch (_) {
        if (cancelled) return;
        setPdfAvailable('no');
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch(mdUrl, { method: 'GET' });
        if (cancelled) return;
        if (!res.ok) {
          setMdState({ status: 'missing', text: '' });
          return;
        }
        const text = await res.text();
        if (cancelled) return;
        setMdState({ status: 'ready', text });
      } catch (_) {
        if (cancelled) return;
        setMdState({ status: 'missing', text: '' });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [mdUrl]);

  const rendered = useMemo(() => {
    if (mdState.status !== 'ready') return null;
    const blocks = parseMarkdown(mdState.text);
    return splitResumeBlocks(blocks);
  }, [mdState.status, mdState.text]);

  const view = VIEW[mode] || VIEW.work;
  const inlineLinkMeta = useMemo(
    () => ({
      onLinkClick: ({ label, url }) => {
        track('resume_inline_link_click', {
          surface: 'resume_markdown',
          resume_mode: mode,
          md_file: mdFile,
          link_label: label,
          link_url: url,
        });
      },
    }),
    [mdFile, mode]
  );

  const visibleBlocks = useMemo(() => {
    if (!rendered?.body) return [];

    const sections = splitByH2(rendered.body);
    const map = new Map(sections.map((s) => [s.key, s.blocks]));

    const main = map.get(view.key) || [];

    // Summary is rendered in /about. Keep the resume views focused on the selected section.
    return main;
  }, [rendered, view.key]);

  return (
    <section className="section section-card resume">
      <div className="resume-header">
        <div className="section-head">
          <h2 className="section-title">
            Resume{' '}
            {pdfAvailable === 'no' ? null : (
              <span className="resume-title-note">(PDF still available)</span>
            )}
          </h2>
        </div>

        <div className="resume-actions">
          <a
            className="card-link resume-action"
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              track('resume_pdf_open_click', {
                surface: 'resume_actions',
                resume_mode: mode,
                md_file: mdFile,
                pdf_available: pdfAvailable,
              });
            }}
          >
            Open PDF
          </a>
          <a
            className="card-link resume-action"
            href={pdfUrl}
            download
            onClick={() => {
              track('resume_pdf_download_click', {
                surface: 'resume_actions',
                resume_mode: mode,
                md_file: mdFile,
                pdf_available: pdfAvailable,
              });
            }}
          >
            Download
          </a>
        </div>
      </div>

      {mdState.status === 'ready' && rendered ? (
        visibleBlocks.length > 0 ? (
          <div className="resume-md" aria-label="Resume content">
            {visibleBlocks.map((block, i) => {
              switch (block.type) {
                case 'h1':
                  return (
                    <h1 key={`b-${i}`} className="resume-h1">
                      {parseInline(block.text, `b-${i}`, inlineLinkMeta)}
                    </h1>
                  );
                case 'h2':
                  return (
                    <h3 key={`b-${i}`} className="resume-section">
                      {parseInline(block.text, `b-${i}`, inlineLinkMeta)}
                    </h3>
                  );
                case 'h3':
                  return (
                    <h4 key={`b-${i}`} className="resume-h3">
                      {parseInline(block.text, `b-${i}`, inlineLinkMeta)}
                    </h4>
                  );
                case 'p':
                  return (
                    <p key={`b-${i}`} className="resume-p">
                      {parseInline(block.text, `b-${i}`, inlineLinkMeta)}
                    </p>
                  );
                case 'ul':
                  return (
                    <ul key={`b-${i}`} className="resume-ul">
                      {block.items.map((item, j) => (
                        <li key={`b-${i}-li-${j}`} className="resume-li">
                          {parseInline(item, `b-${i}-li-${j}`, inlineLinkMeta)}
                        </li>
                      ))}
                    </ul>
                  );
                case 'hr':
                  return <hr key={`b-${i}`} className="resume-hr" />;
                default:
                  return null;
              }
            })}
          </div>
        ) : (
          <p className="section-muted">
            Could not find “{view.label}” in `public/{mdFile}`.
          </p>
        )
      ) : mdState.status === 'missing' ? (
        <p className="section-muted">No `public/{mdFile}` found.</p>
      ) : (
        <p className="section-muted">Loading resume...</p>
      )}

      {pdfAvailable === 'no' ? <p className="resume-note">No `public/resume.pdf` found.</p> : null}
    </section>
  );
};

export default Resume;
